import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { kind: "practice", title: "Practice", date: "", start_time: "17:30", end_time: "", location: "", notes: "", slots: [] };
const PRESETS = [["Snacks", 1], ["Drinks", 1], ["Carpool", 3]];

export const EventForm = ({ initial, onClose, onSave }) => {
  const [f, setF] = useState({ ...EMPTY, ...(initial || {}), end_time: initial?.end_time || "", location: initial?.location || "", notes: initial?.notes || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const slots = f.slots || [];
  const setSlot = (i, patch) => setF({ ...f, slots: slots.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const addSlot = (label, capacity) => setF({ ...f, slots: [...slots, { label, capacity }] });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave({ ...f, slots: slots.filter((s) => s.label.trim()).map((s) => ({ id: s.id, label: s.label.trim(), capacity: Number(s.capacity) || 1 })), title: f.title.trim(), end_time: f.end_time || null, location: f.location.trim() || null, notes: f.notes.trim() || null });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="event-form">
        <DialogHeader><DialogTitle className="font-heading text-2xl uppercase">{initial?.id ? "Edit event" : "New event"}</DialogTitle></DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <select data-testid="event-kind-select" className="field-input w-full" value={f.kind}
            onChange={(e) => setF({ ...f, kind: e.target.value, title: f.title === "Practice" || f.title === "Game" || !f.title ? (e.target.value === "game" ? "Game" : e.target.value === "practice" ? "Practice" : "") : f.title })}>
            <option value="practice">Practice</option>
            <option value="game">Game</option>
            <option value="other">Other event</option>
          </select>
          <input data-testid="event-title-input" className="field-input w-full" required maxLength={80} placeholder="Title, e.g. Game vs Tigers" value={f.title} onChange={set("title")} />
          <input data-testid="event-date-input" type="date" className="field-input w-full" required value={f.date} onChange={set("date")} />
          <div className="grid grid-cols-2 gap-2">
            <label className="space-y-1"><span className="ctl-label">Start</span>
              <input data-testid="event-start-input" type="time" className="field-input w-full" required value={f.start_time} onChange={set("start_time")} /></label>
            <label className="space-y-1"><span className="ctl-label">End (optional)</span>
              <input data-testid="event-end-input" type="time" className="field-input w-full" value={f.end_time} onChange={set("end_time")} /></label>
          </div>
          <input data-testid="event-location-input" className="field-input w-full" maxLength={120} placeholder="Location (optional)" value={f.location} onChange={set("location")} />
          <textarea data-testid="event-notes-input" className="field-input w-full min-h-[80px]" maxLength={1000} placeholder="Notes for parents: arrival time, uniform, snacks..." value={f.notes} onChange={set("notes")} />
          <div className="space-y-2" data-testid="event-slots-editor">
            <span className="ctl-label">Parent signups (snacks, carpool...)</span>
            {slots.map((s, i) => (
              <div key={s.id || i} className="flex gap-2 items-center">
                <input data-testid={`slot-label-input-${i}`} className="field-input flex-1 min-w-0 !min-h-[44px]" maxLength={40} value={s.label} onChange={(e) => setSlot(i, { label: e.target.value })} />
                <input data-testid={`slot-capacity-input-${i}`} type="number" min={1} max={10} className="field-input w-20 !min-h-[44px]" value={s.capacity} onChange={(e) => setSlot(i, { capacity: e.target.value })} aria-label="Spots" />
                <button type="button" aria-label="Remove slot" data-testid={`slot-remove-${i}`} onClick={() => setF({ ...f, slots: slots.filter((_, j) => j !== i) })}><X size={18} /></button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              {PRESETS.map(([l, c]) => (
                <button type="button" key={l} className="btn-ghost !min-h-[40px] !text-sm" data-testid={`slot-add-${l.toLowerCase()}`} onClick={() => addSlot(l, c)}><Plus size={16} /> {l}</button>
              ))}
              <button type="button" className="btn-ghost !min-h-[40px] !text-sm" data-testid="slot-add-custom" onClick={() => addSlot("", 1)}><Plus size={16} /> Other</button>
            </div>
          </div>
          <button className="btn-primary w-full" data-testid="event-save-button" disabled={busy}>Save event</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
